import type { FurniturePart, PlacedModule, SectionalModuleId, Vec2 } from "../types";
import { clamp, part, rect, rotate, translate } from "./geometry";

/**
 * Sectional modules for the modular builder. Each module is authored in its
 * own local frame (origin at module center, +y = front, back along -y) and
 * exposes *ports*: connector points on its edges with an outward normal.
 * Two modules snap together when a port of one coincides with a port of the
 * other and their normals oppose.
 */

export interface ModulePort {
  /** Port center, module-local inches. */
  x: number;
  y: number;
  /** Outward unit normal. */
  nx: number;
  ny: number;
}

export interface SectionalModuleDef {
  id: SectionalModuleId;
  label: string;
  /** Module footprint size (x extent, y extent), inches. */
  w: number;
  d: number;
  ports: ModulePort[];
  /** Build parts in module-local frame. */
  build: (h: number) => FurniturePart[];
  /** Footprint outline in module-local frame (defaults to the rect). */
  footprint?: Vec2[];
}

const SEAT_Z = 18;
const BACK = 9;
const ARM = 7;

function seatCore(w: number, d: number, h: number, hasBack: boolean, armL: number, armR: number): FurniturePart[] {
  const seatH = Math.min(SEAT_Z, h * 0.55);
  const parts: FurniturePart[] = [];
  parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, seatH, { cornerRadius: 1.5 }));
  const back = hasBack ? BACK : 0;
  if (hasBack) {
    parts.push(part("backrest", rect(-w / 2 + armL, -d / 2, w - armL - armR, BACK), seatH, h, { cornerRadius: 1.5 }));
  }
  if (armL) parts.push(part("arm", rect(-w / 2, -d / 2, ARM, d), seatH, h * 0.78, { cornerRadius: 2 }));
  if (armR) parts.push(part("arm", rect(w / 2 - ARM, -d / 2, ARM, d), seatH, h * 0.78, { cornerRadius: 2 }));
  const sx = -w / 2 + armL + 0.75;
  const sw = w - armL - armR - 1.5;
  const sy = -d / 2 + back + 0.75;
  const sd = d - back - 1.5;
  const cells = Math.max(1, Math.round(sd / 30));
  const cellD = (sd - (cells - 1)) / cells;
  for (let i = 0; i < cells; i++) {
    parts.push(
      part(i === 0 ? "seat" : "chaise", rect(sx, sy + i * (cellD + 1), sw, cellD), seatH, seatH + 4, {
        cornerRadius: clamp(Math.min(sw, cellD) * 0.08, 1, 2.5),
      })
    );
  }
  if (hasBack) {
    parts.push(
      part("cushion", rect(sx + 0.5, -d / 2 + 1, sw - 1, BACK - 1.75), h * 0.6, h, {
        cornerRadius: 2.5,
      })
    );
  }
  return parts;
}

export const SECTIONAL_MODULES: Record<SectionalModuleId, SectionalModuleDef> = {
  seat: {
    id: "seat",
    label: "Armless seat",
    w: 30,
    d: 36,
    ports: [
      { x: -15, y: 0, nx: -1, ny: 0 },
      { x: 15, y: 0, nx: 1, ny: 0 },
    ],
    build: (h) => seatCore(30, 36, h, true, 0, 0),
  },
  "seat-arm-left": {
    id: "seat-arm-left",
    label: "Seat, left arm",
    w: 36,
    d: 36,
    ports: [{ x: 18, y: 0, nx: 1, ny: 0 }],
    build: (h) => seatCore(36, 36, h, true, ARM, 0),
  },
  "seat-arm-right": {
    id: "seat-arm-right",
    label: "Seat, right arm",
    w: 36,
    d: 36,
    ports: [{ x: -18, y: 0, nx: -1, ny: 0 }],
    build: (h) => seatCore(36, 36, h, true, 0, ARM),
  },
  corner: {
    id: "corner",
    label: "Corner",
    w: 36,
    d: 36,
    // Backs along -y and -x; open toward +x (main run) and +y (return).
    ports: [
      { x: 18, y: 0, nx: 1, ny: 0 },
      { x: 0, y: 18, nx: 0, ny: 1 },
    ],
    build: (h) => {
      const seatH = Math.min(SEAT_Z, h * 0.55);
      const parts: FurniturePart[] = [];
      parts.push(part("body", rect(-18, -18, 36, 36), 0, seatH, { cornerRadius: 1.5 }));
      parts.push(part("backrest", rect(-18, -18, 36, BACK), seatH, h, { cornerRadius: 1.5 }));
      parts.push(part("backrest", rect(-18, -18 + BACK, BACK, 36 - BACK), seatH, h, { cornerRadius: 1.5 }));
      parts.push(part("seat", rect(-18 + BACK + 0.75, -18 + BACK + 0.75, 36 - BACK - 1.5, 36 - BACK - 1.5), seatH, seatH + 4, { cornerRadius: 1.5 }));
      parts.push(part("cushion", rect(-18 + BACK + 0.75, -17, 36 - BACK - 1.5, BACK - 1.75), h * 0.6, h, { cornerRadius: 2.5 }));
      parts.push(part("cushion", rect(-17, -18 + BACK + 0.75, BACK - 1.75, 36 - BACK - 1.5), h * 0.6, h, { cornerRadius: 2.5 }));
      return parts;
    },
  },
  "corner-curved": {
    id: "corner-curved",
    label: "Curved corner",
    w: 36,
    d: 36,
    ports: [
      { x: 18, y: 0, nx: 1, ny: 0 },
      { x: 0, y: 18, nx: 0, ny: 1 },
    ],
    footprint: curvedCornerOutline(36),
    build: (h) => {
      const seatH = Math.min(SEAT_Z, h * 0.55);
      const parts: FurniturePart[] = [];
      parts.push(part("body", curvedCornerOutline(36), 0, seatH));
      parts.push(part("backrest", curvedCornerOutline(36, BACK), seatH, h));
      parts.push(part("seat", translate(curvedCornerOutline(36 - BACK - 1.5, 0), (BACK + 0.75) / 2, (BACK + 0.75) / 2), seatH, seatH + 4));
      return parts;
    },
  },
  "chaise-left": {
    id: "chaise-left",
    label: "Chaise, left arm",
    w: 34,
    d: 64,
    ports: [{ x: 17, y: -14, nx: 1, ny: 0 }],
    build: (h) => seatCore(34, 64, h, true, ARM, 0),
  },
  "chaise-right": {
    id: "chaise-right",
    label: "Chaise, right arm",
    w: 34,
    d: 64,
    ports: [{ x: -17, y: -14, nx: -1, ny: 0 }],
    build: (h) => seatCore(34, 64, h, true, 0, ARM),
  },
  ottoman: {
    id: "ottoman",
    label: "Ottoman",
    w: 30,
    d: 30,
    ports: [
      { x: -15, y: 0, nx: -1, ny: 0 },
      { x: 15, y: 0, nx: 1, ny: 0 },
      { x: 0, y: -15, nx: 0, ny: -1 },
      { x: 0, y: 15, nx: 0, ny: 1 },
    ],
    build: (h) => {
      const parts: FurniturePart[] = [];
      parts.push(part("body", rect(-15, -15, 30, 30), 0, h, { cornerRadius: 2 }));
      parts.push(part("cushion", rect(-13.75, -13.75, 27.5, 27.5), h, h + 2, { cornerRadius: 1.5 }));
      return parts;
    },
  },
};

/** Square with the outer (-x,-y) corner rounded into a quarter circle. When
 *  `band` is set, returns just the L-shaped back band along the two rear edges. */
function curvedCornerOutline(size: number, band = 0): Vec2[] {
  const half = size / 2;
  const r = size * 0.9;
  const out: Vec2[] = [];
  const cx = half;
  const cy = half;
  const segs = 20;
  // Outer arc from (-half, half) sweeping to (half, -half) around (half, half).
  for (let i = 0; i <= segs; i++) {
    const t = Math.PI + (i / segs) * (Math.PI / 2);
    out.push({ x: cx + Math.cos(t) * r, y: cy + Math.sin(t) * r });
  }
  if (band <= 0) {
    out.push({ x: half, y: half });
    return out;
  }
  // Inner arc back.
  const ri = r - band;
  out.push({ x: half, y: -half + band });
  for (let i = segs; i >= 0; i--) {
    const t = Math.PI + (i / segs) * (Math.PI / 2);
    out.push({ x: cx + Math.cos(t) * ri, y: cy + Math.sin(t) * ri });
  }
  return out;
}

export function getModule(id: SectionalModuleId): SectionalModuleDef {
  return SECTIONAL_MODULES[id];
}

/** Module footprint transformed into the piece's local frame. */
export function placedModuleOutline(m: PlacedModule): Vec2[] {
  const def = getModule(m.moduleId);
  const local = def.footprint ?? rect(-def.w / 2, -def.d / 2, def.w, def.d);
  return translate(rotate(local, m.rotation), m.x, m.y);
}

/** Module parts transformed into the piece's local frame. */
export function placedModuleParts(m: PlacedModule, h: number): FurniturePart[] {
  const def = getModule(m.moduleId);
  return def.build(h).map((p) => ({
    ...p,
    id: `${m.id}:${p.id}`,
    outline: translate(rotate(p.outline, m.rotation), m.x, m.y),
    seams: p.seams?.map((s) => {
      const [a, b] = translate(rotate([s.a, s.b], m.rotation), m.x, m.y);
      return { a, b };
    }),
  }));
}

/** World-frame ports of a placed module (piece-local coordinates). */
export function placedModulePorts(m: PlacedModule): ModulePort[] {
  const def = getModule(m.moduleId);
  const rad = (m.rotation * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return def.ports.map((p) => ({
    x: m.x + p.x * c - p.y * s,
    y: m.y + p.x * s + p.y * c,
    nx: p.nx * c - p.ny * s,
    ny: p.nx * s + p.ny * c,
  }));
}

/** Default L-shaped starting arrangement for a new modular sectional:
 *  corner at the back-left, two armless seats running right (open end, so
 *  the user can keep extending), and an arm seat closing the return. */
export function defaultSectionalModules(): PlacedModule[] {
  return [
    { id: "m1", moduleId: "corner", x: 0, y: 0, rotation: 0 },
    { id: "m2", moduleId: "seat", x: 33, y: 0, rotation: 0 },
    { id: "m3", moduleId: "seat", x: 63, y: 0, rotation: 0 },
    // Rotated -90° so its left arm points toward +y (the open end) and its
    // single port meets the corner's +y port.
    { id: "m4", moduleId: "seat-arm-left", x: 0, y: 36, rotation: 270 },
  ];
}
